import { Box, Theme } from "@radix-ui/themes";
import {
	HeadContent,
	Outlet,
	Scripts,
	createRootRoute,
} from "@tanstack/react-router";
import type { ReactNode } from "react";
import { Footer } from "~/components/Footer";
import { LocalizationProvider } from "~/components/localization/LocalizationProvider";
import { getLocalePreference } from "~/components/localization/locale.functions";
import { Navigation } from "~/components/Navigation";
// Keep all component/utility behavior, but only load the palettes used by MOS.
import "@radix-ui/themes/tokens/base.css";
import "@radix-ui/themes/tokens/colors/indigo.css";
import "@radix-ui/themes/tokens/colors/slate.css";
import "@radix-ui/themes/tokens/colors/red.css";
import "@radix-ui/themes/components.css";
import "@radix-ui/themes/utilities.css";
import "~/root.css";

export const Route = createRootRoute({
	loader: () => getLocalePreference(),
	head: () => ({
		meta: [
			{ charSet: "utf-8" },
			{ name: "viewport", content: "width=device-width, initial-scale=1" },
		],
		links: [
			{
				rel: "icon",
				href: "/favicon.png",
				type: "image/png",
			},
		],
	}),
	component: AppShell,
	shellComponent: RootDocument,
});

const criticalFontCss = `
@font-face {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url("/fonts/poppins-latin-400.woff2") format("woff2");
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}

@font-face {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 400;
  font-display: swap;
  src: url("/fonts/poppins-latin-ext-400.woff2") format("woff2");
  unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
}

@font-face {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url("/fonts/poppins-latin-700.woff2") format("woff2");
  unicode-range: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+0304, U+0308, U+0329, U+2000-206F, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF, U+FFFD;
}

@font-face {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 700;
  font-display: swap;
  src: url("/fonts/poppins-latin-ext-700.woff2") format("woff2");
  unicode-range: U+0100-02BA, U+02BD-02C5, U+02C7-02CC, U+02CE-02D7, U+02DD-02FF, U+0304, U+0308, U+0329, U+1D00-1DBF, U+1E00-1E9F, U+1EF2-1EFF, U+2020, U+20A0-20AB, U+20AD-20C0, U+2113, U+2C60-2C7F, U+A720-A7FF;
}
`;

function AppShell() {
	const { locale } = Route.useLoaderData();

	return (
		<Theme
			style={{
				width: "100%",
				minHeight: "100%",
			}}
			appearance="light"
			panelBackground="solid"
			radius="none"
		>
			<LocalizationProvider initialLocale={locale}>
				<Navigation />
				<Box pt="110px" asChild>
					<main>
						<Outlet />
					</main>
				</Box>
				<Footer />
			</LocalizationProvider>
		</Theme>
	);
}

function RootDocument({ children }: { children: ReactNode }) {
	const { locale } = Route.useLoaderData();

	return (
		<html lang={locale}>
			<head>
				<link
					rel="preload"
					href="/fonts/poppins-latin-400.woff2"
					as="font"
					type="font/woff2"
					crossOrigin="anonymous"
				/>
				<link
					rel="preload"
					href="/fonts/poppins-latin-700.woff2"
					as="font"
					type="font/woff2"
					crossOrigin="anonymous"
				/>
				<style>{criticalFontCss}</style>
				<HeadContent />
			</head>
			<body>
				{children}
				<Scripts />
			</body>
		</html>
	);
}
